/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useReducer, useState } from 'react';

// FROM: https://usehooks.com/useLocalStorage/
export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue as T, setValue as any];
}
const createReducer = (initialState) => {
  return (state, action) => {
    switch (action.type) {
      case 'reset':
        return initialState;
      case 'update':
        const { type, matches } = action.payload;
        return { ...state, [type]: matches };
      default:
        return state;
    }
  };
};
// FROM: https://codesandbox.io/s/6wxr1nyjxw?file=/src/useBreakpoint.js:71-2202
export const useBreakpoint = (breakpoints = {}) => {
  const toMediaQuery = (size) => window.matchMedia(`(min-width: ${size}px)`);
  const parseBreakpoints = () => Object.keys(breakpoints).sort((a, b) => breakpoints[b] - breakpoints[a]);

  const initialState = Object.keys(breakpoints).reduce((prev, curr) => {
    return { ...prev, [curr]: toMediaQuery(breakpoints[curr].size).matches };
  }, {});
  const reducer = createReducer(initialState);
  const [activeBreakpoint, setActiveBreakpoint] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  const update = useCallback((payload) => dispatch({ type: 'update', payload }), [dispatch]);

  const handleOnMediaQueryMatch = (mq, type) => {
    update({ type, matches: mq.matches });
  };

  const mediaQueries = parseBreakpoints();

  useEffect(() => {
    const onChange = (mq, mediaQuery) => {
      handleOnMediaQueryMatch(mq, mediaQuery);
    };

    mediaQueries.forEach((mediaQuery) => {
      const mq = toMediaQuery(breakpoints[mediaQuery]);
      mq.addListener(() => {
        onChange(mq, mediaQuery);
      });
    });

    return () => {
      mediaQueries.forEach((mediaQuery) => {
        const mq = toMediaQuery(breakpoints[mediaQuery]);
        mq.removeListener(() => {
          onChange(mq, mediaQuery);
        });
      });
    };
  }, []);

  useEffect(() => {
    let result = null;
    mediaQueries.some((mq) => {
      const isActive = state[mq];
      if (isActive) result = mq;
      return isActive;
    });
    setActiveBreakpoint(result);
  }, [state]);

  return activeBreakpoint;
};
